import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import { useTranslation } from 'react-i18next';
import * as actions from '../../actions';
import { selectors } from '../../reducer';

import * as Styled from './RecipeInfoBlock.styles';
import Loader from '../../../../components/Loader';
import MedicinesListBlock from '../MedicinesListBlock';
import ApplyRecipeInfo from '../ApplyRecipeInfo';
import RxGateChosenReceiptBlock from '../RxGateChosenReceiptBlock';
import RxGateAvailableReceiptsBlock from '../RxGateAvailableReceiptsBlock';

const RecipeInfoBlock: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [medicinesOpen, setMedicinesOpen] = React.useState(false);

  const recipeInfo = useSelector(selectors.recipeInfo);
  const recipeInfoError = useSelector(selectors.recipeInfoError);
  const recipeInfoLoading = useSelector(selectors.recipeInfoLoading);

  const rxGateCurrentReceipt = useSelector(selectors.rxGateCurrentReceipt);
  const rxGateCurrentReceiptLoading = useSelector(
    selectors.rxGateCurrentReceiptLoading
  );
  const rxGateCurrentReceiptError = useSelector(
    selectors.rxGateCurrentReceiptError
  );
  const rxGateChosenReceipt = useSelector(selectors.rxGateChosenReceipt);

  const formatRecipeInfoValue = (value: unknown) => {
    if (value === null || value === undefined) {
      return '-';
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().split('T')[0];
    }

    if (typeof value === 'string') {
      // ISO datetime -> YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}T/.test(value)) {
        return value.split('T')[0];
      }
      return value;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      const obj = value as { name?: unknown };

      if (typeof obj.name === 'string' && obj.name.trim()) {
        return obj.name;
      }

      return '-';
    }

    return String(value);
  };

  const handleGetSuitableMedications = () => {
    dispatch(
      actions.getSuitableMedications.request({
        nameCodeList: recipeInfo?.tradeName?.tradeNameUuid,
      })
    );
    dispatch(actions.getRxGateCurrentReceipt.request());
    setMedicinesOpen(true);
  };

  useEffect(() => {
    if (!medicinesOpen) {
      return;
    }
    const intervalId = setInterval(() => {
      dispatch(actions.getRxGateCurrentReceipt.request());
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [medicinesOpen]);

  return (
    <Styled.RecipeInfoBlock>
      <div>{t('RXGatePage.RECIPE_INFO')}</div>
      <div className="recipe-info-block">
        {!recipeInfo && recipeInfoLoading && <Loader />}
        {recipeInfoError && <div>{recipeInfoError}</div>}
        {recipeInfo && (
          <>
            <div className="recipe-info">
              {Object.entries(recipeInfo ?? {}).map(([key, value]) => (
                <div className="recipe-info-item" key={key}>
                  {t(`RXGatePage.RecipeInfo.${key.toUpperCase()}`)}:{' '}
                  <strong>{formatRecipeInfoValue(value) || '-'}</strong>
                </div>
              ))}
            </div>
            <Styled.ApplyProductsButton onClick={handleGetSuitableMedications}>
              {t('RXGatePage.SUITABLE_PRODUCTS')}
            </Styled.ApplyProductsButton>
          </>
        )}
      </div>
      <MedicinesListBlock medicinesOpen={medicinesOpen} />
      {medicinesOpen && recipeInfo && (
        <Styled.RecipeActiveReceipt>
          {rxGateCurrentReceiptLoading && !rxGateCurrentReceipt && (
            <CircularProgress
              size={40}
              color="inherit"
              sx={{ margin: '5px 10px' }}
            />
          )}
          {rxGateCurrentReceiptError && (
            <div className="active-receipt-error">
              {t('LoyaltyPage.RECEIPT_ERROR')}
            </div>
          )}
          {rxGateChosenReceipt ? (
            <div className="receipts-block">
              <RxGateChosenReceiptBlock />
            </div>
          ) : (
            <div className="available-receipts-block">
              <RxGateAvailableReceiptsBlock />
            </div>
          )}
          {rxGateChosenReceipt && (
            <div className="active-receipt-total">
              {t('RXGatePage.TOTAL_AMOUNT')}
              {rxGateChosenReceipt.products.reduce(
                (acc, item) => acc + item.price * item.quantity,
                0
              )}{' '}
              ₸
            </div>
          )}
        </Styled.RecipeActiveReceipt>
      )}
      {rxGateChosenReceipt && <ApplyRecipeInfo />}
    </Styled.RecipeInfoBlock>
  );
};

export default RecipeInfoBlock;
